import axios, { CanceledError } from 'axios'
import type { AxiosInstance } from 'axios'

type Entry = { controller: AbortController; promise: Promise<any>; users: number; at: number; value?: any; epoch: number }
const entries = new Map<string, Entry>()
let epoch = 0, identity = '', page = new AbortController()

export function cancelPageReads() { page.abort(); page = new AbortController() }
export function invalidateTeacherReads() {
  epoch++
  entries.forEach(entry => entry.controller.abort())
  entries.clear()
  queueMicrotask(() => window.dispatchEvent(new Event('teacher:data-invalidated')))
}
function stable(value: any): string {
  if (Array.isArray(value)) return '[' + value.map(stable).join(',') + ']'
  if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(k => JSON.stringify(k) + ':' + stable(value[k])).join(',') + '}'
  return JSON.stringify(value)
}
function policy(url: string): number | null {
  if (/^\/meta(?:\/|$)/.test(url)) return 60000
  if (/^\/(classes|students|questions|homeworks|analytics|error-questions|risk)(?:\/|$)/.test(url)) return 10000
  if (url === '/teacher/dashboard-v2') return 10000
  return null
}

export function installTeacherReads(client: AxiosInstance, scope: () => string) {
  const adapter = axios.getAdapter(client.defaults.adapter)
  client.defaults.adapter = async config => {
    const url = (config.url || '').replace(/^\/api/, '').split('?')[0]
    const ttl = config.method === 'get' ? policy(url) : null
    if (ttl === null) return adapter(config)
    const current = scope()
    if (identity !== current) { identity = current; invalidateTeacherReads() }
    const generation = epoch
    const key = stable([current, config.baseURL, config.url, config.params || {}, config.responseType || 'json'])
    let entry = entries.get(key)
    if ((config as any).teacherReadForce && entry?.value) { entries.delete(key); entry = undefined }
    if (entry?.value && Date.now() - entry.at >= ttl) { entries.delete(key); entry = undefined }
    const clone = (value: any) => ({ ...value, config, data: typeof value.data === 'string' ? value.data : structuredClone(value.data) })
    if (entry?.value) return clone(entry.value)
    const signals = [config.signal, /^\/meta(?:\/|$)/.test(url) ? undefined : page.signal].filter(Boolean) as AbortSignal[]
    if (signals.some(signal => signal.aborted)) throw new CanceledError('Read cancelled')
    if (!entry) {
      if (entries.size >= 96) {
        const old = [...entries].find(([, value]) => value.value)
        if (old) entries.delete(old[0])
      }
      const controller = new AbortController()
      const created: Entry = { controller, users: 0, at: 0, epoch: generation, promise: Promise.resolve() }
      created.promise = adapter({ ...config, signal: controller.signal }).then(value => {
        if (scope() !== current || epoch !== generation) throw new CanceledError('Read scope changed')
        created.value = value; created.at = Date.now()
        return value
      }).catch(error => { if (entries.get(key) === created) entries.delete(key); throw error })
      entry = created; entries.set(key, entry)
    }
    const shared = entry
    shared.users++
    return new Promise<any>((resolve, reject) => {
      let done = false
      const finish = (error?: any, value?: any) => {
        if (done) return
        done = true; shared.users--
        signals.forEach(signal => signal.removeEventListener('abort', abort))
        if (!shared.users && !shared.value) { shared.controller.abort(); if (entries.get(key) === shared) entries.delete(key) }
        if (error) reject(error); else resolve(clone(value))
      }
      const abort = () => finish(new CanceledError('Read cancelled'))
      signals.forEach(signal => signal.addEventListener('abort', abort, { once: true }))
      shared.promise.then(value => finish(undefined, value), error => finish(error))
    })
  }
  client.interceptors.response.use(response => {
    if (!['get', 'head', 'options'].includes(response.config.method || 'get')) invalidateTeacherReads()
    return response
  })
}
